import { formatBotResponse, type Citation } from "@/common/services/formatBotResponse";
import styles from "./ChatMessage.module.css";
import ReactMarkdown from "react-markdown";
import Disclaimer from "../Disclaimer/Disclaimer";
import QuickReplyButtons from "../QuickReplyButtons/QuickReplyButtons";
import type { LinkButtonItem, QuickReplyOption } from "@/common/types/chat.types";
import { useChatContext } from "@/common/context/ChatContext";

interface ChatMessageProps {
  sender: string;
  content: string;
  type: "user" | "bot" | "agent" | "system";
  citations?: Citation[];
  linkButtonItems: LinkButtonItem[] | null;
  quickReplies?: QuickReplyOption[];
  disclaimerText?: string;
  timestamp?: string;
  agentName?: string;
  onSelectQuickReply?: (value: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  sender,
  content,
  type,
  citations = [],
  linkButtonItems = [],
  quickReplies = undefined,
  disclaimerText = undefined,
  timestamp,
  agentName,
  onSelectQuickReply,
}) => {
  const { config } = useChatContext();
  const markdown = formatBotResponse(content, citations);

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageTypeClass = () => {
    if (type === "user") return styles.user;
    if (type === "system") return styles.system;
    if (type === "agent") return styles.agent;
    return "";
  };

  const getSenderDisplayName = () => {
    if (type === "user") return "Du";
    if (type === "system") return null;
    if (type === "agent") return agentName || sender;
    if (type === "bot") return "Hjälprobot";
    return sender;
  };

  const LinkNewTabRenderer = ({ href, children, ...props }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Länk till källa, öppnas i ny flik"
      {...props}
    >
      {children}
    </a>
  );

  const hasContent = content && content.trim().length > 0;
  const hasCitations = type === "bot" && citations.length > 0;

  return (
    <section className={`${styles.messageBlock} ${getMessageTypeClass()}`}>
      {getSenderDisplayName() && (
        <p className={styles.sender}>
          {getSenderDisplayName()}
          {timestamp && type === "agent" && (
            <span className={styles.timestamp}> • {formatTimestamp(timestamp)}</span>
          )}
        </p>
      )}
      {hasContent && (
        <div className={styles.message}>
          {hasContent && (
            <ReactMarkdown components={config.openLinksInNewTab ? { a: LinkNewTabRenderer } : undefined}>
              {markdown}
            </ReactMarkdown>
          )}
          {hasCitations && (
            <div className={styles.sources}>
              <p><strong>Källor:</strong></p>
              <ul>
                {citations.map((citation) => (
                  <li key={citation.anchor} id={`ref-${citation.anchor}`}>
                    <a href={citation.source} target="_blank" rel="noopener noreferrer" aria-label={`Läs källa: ${citation.title}, öppnas i ny flik`}>
                      {citation.anchor}. {citation.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {type === "bot" && (
        <div className={styles.linkButtonWrapper}>
          {linkButtonItems?.map((item) => (
              <a
                key={item.title}
                className={styles.linkButton}
                href={item.url}
                target={item.target}
                rel="noopener noreferrer"
                aria-label={`Länk till: ${item.title}${item.target === '_blank' ? ', öppnas i ny flik' : ''}`}
              >
                  {item.title}
              </a>
          ))}
        </div>
      )}
      {type === "bot" && quickReplies && quickReplies.length > 0 && (
        <QuickReplyButtons
          options={quickReplies}
          onSelect={(value) => {
            if (onSelectQuickReply) {
              onSelectQuickReply(value);
            }
          }}
        />
      )}
      {disclaimerText && type === "bot" && (
        <Disclaimer disclaimerText={disclaimerText} />
      )}
    </section>
  );
};

export default ChatMessage;
